import type {
  Material,
  MaterialKind,
  PhongMaterial,
  PhysicalMaterial,
  StandardMaterial,
  TextureUUID,
  Vector2,
  Vector4,
} from "../../types/scene";
import { clampToRange } from "./attributeUtils";
import type { ParsedMaterialsMap } from "./types";

type MaterialParser = {
  xmlDoc: Document;
  materialClassDefaults: Record<string, Record<string, unknown> | undefined>;
  materialDefaultScope: Record<string, unknown>;
};

export const convertSpecularShininessToMetallicRoughness = (
  specular: number,
  shininess: number
): { metallic: number; roughness: number } => {
  const metallic = Math.min(specular * 1.5, 1.0);
  const roughness = Math.max(0.0, 0.5 - (shininess - 0.5));
  return { metallic, roughness };
};

export const parseColorVector = (value: string | null): Vector4 | undefined => {
  if (!value) return undefined;
  const components = value
    .trim()
    .split(/\s+/)
    .map((component) => Number(component));
  if (components.length === 0) return undefined;
  const color: Vector4 = [1, 1, 1, 1];
  components.slice(0, 4).forEach((component, index) => {
    if (Number.isFinite(component)) {
      color[index] = component;
    }
  });
  return color;
};

export const resolveMaterialProp = (
  parser: MaterialParser,
  materialElem: Element,
  prop: string
): number | undefined => {
  if (materialElem.getAttribute(prop)) {
    return Number(materialElem.getAttribute(prop));
  }

  let className = materialElem.getAttribute("class");
  const visited = new Set();
  while (className && !visited.has(className)) {
    visited.add(className);
    const def = parser.materialClassDefaults[className];
    const value = def?.[prop];
    if (typeof value === "number") return value;
    const parentClass = def?.parentClass;
    className = typeof parentClass === "string" ? parentClass : null;
  }

  const defaultValue = parser.materialDefaultScope[prop];
  if (typeof defaultValue === "number") {
    return defaultValue;
  }

  return undefined;
};

export const parseMaterials = (rawParser: unknown): ParsedMaterialsMap => {
  const parser = rawParser as MaterialParser;
  const parsedMaterials: ParsedMaterialsMap = {};

  const materialElements = parser.xmlDoc.querySelectorAll(
    "asset > material"
  ) as NodeListOf<Element>;
  const textureSourceByName = new Map<string, string>();
  (parser.xmlDoc.querySelectorAll("asset > texture") as NodeListOf<Element>).forEach((texture) => {
    const name = texture.getAttribute("name");
    if (!name) return;
    textureSourceByName.set(name, texture.getAttribute("file") || "");
  });
  const textureMayCarryAlpha = (textureName?: string): boolean => {
    if (!textureName) return false;
    const file = textureSourceByName.get(textureName) || "";
    const source = `${textureName} ${file}`.toLowerCase();
    return source.includes("albedotransparency") || source.includes("transparency");
  };

  materialElements.forEach((mat: Element) => {
    const name = mat.getAttribute("name");
    if (!name) return;

    const rgbaValues = mat
      .getAttribute("rgba")
      ?.trim()
      .split(/\s+/)
      .map((value: string) => Number(value));

    const color: Vector4 = [1, 1, 1, 1];
    if (Array.isArray(rgbaValues)) {
      rgbaValues.slice(0, 4).forEach((component, index) => {
        if (Number.isFinite(component)) {
          color[index] = component;
        }
      });
    }

    const textureName = mat.getAttribute("texture") || undefined;
    const texrepeat = mat
      .getAttribute("texrepeat")
      ?.trim()
      .split(/\s+/)
      .map((value: string) => Number(value))
      .filter((value: number) => Number.isFinite(value));
    const texuniform = mat.getAttribute("texuniform");
    const emissionAttr = mat.getAttribute("emission");
    const emission = emissionAttr != null ? Number(emissionAttr) : undefined;
    const emissiveValue = Number.isFinite(emission) ? (emission as number) : undefined;
    const emissiveColor: Vector4 | undefined =
      emissiveValue && emissiveValue > 0
        ? [emissiveValue, emissiveValue, emissiveValue, 1]
        : undefined;

    const specularAttr = mat.getAttribute("specular");
    const shininessAttr = mat.getAttribute("shininess");
    const roughnessAttr = mat.getAttribute("roughness");
    const metallicAttr = mat.getAttribute("metallic");
    const reflectanceAttr = mat.getAttribute("reflectance");

    // Check for inherited properties
    const inheritedSpecular = resolveMaterialProp(parser, mat, "specular");
    const inheritedShininess = resolveMaterialProp(parser, mat, "shininess");
    const inheritedRoughness = resolveMaterialProp(parser, mat, "roughness");
    const inheritedMetallic = resolveMaterialProp(parser, mat, "metallic");

    // Determine material kind heuristically based on available attributes (including inherited)
    const hasPhysicalSignals = [
      "clearcoat",
      "clearcoatroughness",
      "transmission",
      "thickness",
      "ior",
      "specularcolor",
      "specularintensity",
      "anisotropy",
      "iridescence",
    ].some((attr) => mat.hasAttribute(attr));

    // hasStandardSignals unused; kind inference is driven by physical/PBR attrs
    // hasPhongSignals unused; kind inference is driven by physical/PBR attrs

    let targetKind: MaterialKind = "standard"; // Default to modern PBR materials
    if (hasPhysicalSignals) {
      targetKind = "physical";
    }
    // Phong and standard signals get converted to standard PBR
    // Materials with just color default to standard PBR

    const textureHasAlpha = textureMayCarryAlpha(textureName);
    const materialTransparent = color[3] < 1 || textureHasAlpha;
    const colorMapRepeat: Vector2 | undefined =
      texrepeat && texrepeat.length >= 2
        ? texuniform === "true" || texuniform === "1"
          ? [texrepeat[0], texrepeat[0]]
          : [texrepeat[0], texrepeat[1]]
        : undefined;
    const baseProps = {
      name,
      colorMap: textureName as TextureUUID | undefined,
      color,
      transparent: materialTransparent,
      alphaMode: (materialTransparent ? "blend" : "opaque") as "blend" | "opaque",
      alphaCutoff: 0.5,
      doubleSided: true,
      colorMapWrap: (texrepeat && texrepeat.length >= 2 ? "repeat" : "clamp") as "repeat" | "clamp",
      colorMapRepeat,
      opacity: color[3],
      emissive: emissiveColor,
      emissiveIntensity: emissiveValue,
      ...(reflectanceAttr != null ? { reflectivity: Number(reflectanceAttr) } : {}),
    };

    const directMetallic = metallicAttr != null ? Number(metallicAttr) : inheritedMetallic;
    const directRoughness = roughnessAttr != null ? Number(roughnessAttr) : inheritedRoughness;

    const specularValue = specularAttr != null ? Number(specularAttr) : inheritedSpecular;
    const shininessValue = shininessAttr != null ? Number(shininessAttr) : inheritedShininess;

    const specularNumber = Number.isFinite(specularValue) ? (specularValue as number) : 0.5;
    const shininessNumber = Number.isFinite(shininessValue) ? (shininessValue as number) : 0.5;

    const { metallic, roughness } = (() => {
      if (Number.isFinite(directMetallic) && Number.isFinite(directRoughness)) {
        return {
          metallic: directMetallic as number,
          roughness: directRoughness as number,
        };
      }
      const hasLegacySurfaceSignals =
        Number.isFinite(specularValue) || Number.isFinite(shininessValue);
      if (!hasLegacySurfaceSignals) {
        return {
          metallic: 0,
          roughness: 0.5,
        };
      }
      const converted = convertSpecularShininessToMetallicRoughness(
        Number.isFinite(specularValue) ? (specularValue as number) : 0.5,
        Number.isFinite(shininessValue) ? (shininessValue as number) : 0.5
      );
      return converted;
    })();

    const metalnessValue = clampToRange(Number.isFinite(metallic) ? metallic : 0, 0, 1);
    const roughnessValue = clampToRange(Number.isFinite(roughness) ? roughness : 0.5, 0, 1);

    let material: Partial<Material>;
    if (targetKind === "physical") {
      const physical: Partial<PhysicalMaterial> = {
        kind: "physical",
        ...baseProps,
        metalness: metalnessValue,
        roughness: roughnessValue,
        clearcoat: mat.hasAttribute("clearcoat")
          ? Number(mat.getAttribute("clearcoat"))
          : undefined,
        clearcoatRoughness: mat.hasAttribute("clearcoatroughness")
          ? Number(mat.getAttribute("clearcoatroughness"))
          : undefined,
        transmission: mat.hasAttribute("transmission")
          ? Number(mat.getAttribute("transmission"))
          : undefined,
        thickness: mat.hasAttribute("thickness")
          ? Number(mat.getAttribute("thickness"))
          : undefined,
        ior: mat.hasAttribute("ior") ? Number(mat.getAttribute("ior")) : undefined,
        specularIntensity: mat.hasAttribute("specularintensity")
          ? Number(mat.getAttribute("specularintensity"))
          : undefined,
        specularColor: mat.hasAttribute("specularcolor")
          ? parseColorVector(mat.getAttribute("specularcolor"))
          : undefined,
        anisotropy: mat.hasAttribute("anisotropy")
          ? Number(mat.getAttribute("anisotropy"))
          : undefined,
        iridescence: mat.hasAttribute("iridescence")
          ? Number(mat.getAttribute("iridescence"))
          : undefined,
      };
      material = physical;
    } else if (targetKind === "standard") {
      const standard: Partial<StandardMaterial> = {
        kind: "standard",
        ...baseProps,
        metalness: metalnessValue,
        roughness: roughnessValue,
      };
      material = standard;
    } else {
      const phong: Partial<PhongMaterial> = {
        kind: "phong",
        ...baseProps,
        specular: [specularNumber, specularNumber, specularNumber, 1],
        shininess:
          shininessValue != null && Number.isFinite(shininessValue) ? shininessNumber : undefined,
        reflectivity: mat.hasAttribute("reflectance")
          ? Number(mat.getAttribute("reflectance"))
          : undefined,
        refractionRatio: mat.hasAttribute("refractive")
          ? Number(mat.getAttribute("refractive"))
          : undefined,
      };
      material = phong;
    }

    parsedMaterials[name] = material;
  });

  return parsedMaterials;
};
